"use client";

import React, { useState, useCallback, useMemo } from "react";
import Image, { ImageProps } from "next/image";

/** Default fallback image when src is empty or fails to load. */
export const DEFAULT_IMAGE_FALLBACK = "/images/placeholder-item.jpg";

export type AppImageProps = Omit<ImageProps, "src"> & {
  /** Image URL. When empty string, null, or undefined, fallback is used. */
  src: string | null | undefined;
  /**
   * Fallback image URL when src is empty or on load error.
   * @default DEFAULT_IMAGE_FALLBACK
   */
  fallback?: string;
};

/**
 * Reusable Image component that never passes an empty string to Next.js Image.
 * Uses a fallback image when src is missing or invalid, avoiding the browser
 * "empty string passed to src" issue and failed loads.
 */
function AppImageComponent({
  src,
  fallback = DEFAULT_IMAGE_FALLBACK,
  alt,
  onError,
  ...rest
}: AppImageProps) {
  const rawSrc = typeof src === "string" ? src.trim() : "";
  const resolvedFromProp = rawSrc || fallback;

  const [useFallbackDueToError, setUseFallbackDueToError] = useState(false);

  const displaySrc = useMemo(
    () => (useFallbackDueToError ? fallback : resolvedFromProp),
    [useFallbackDueToError, fallback, resolvedFromProp]
  );

  const handleError = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
      setUseFallbackDueToError(true);
      onError?.(e);
    },
    [onError]
  );

  // When src prop changes to a new valid URL, allow trying it again
  React.useEffect(() => {
    if (rawSrc) setUseFallbackDueToError(false);
  }, [rawSrc]);

  return (
    <Image
      {...rest}
      src={displaySrc}
      alt={alt ?? ""}
      onError={handleError}
    />
  );
}

export const AppImage = React.memo(AppImageComponent);
